import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IMuiSelectOption } from "../components/framework/muiAutocomplete2";
import { getVehicles } from "../services/vehicleService";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { getVehicleGroups } from "../services/vehicleGroupService";
import { VehicleGroupItem } from "../models/vehicleGroup/vehicleGroupItem";
import { VehicleMaintenanceDto } from "../models/vehicle/vehicleMaintenance";
import * as vehicleMaintenanceService from "../services/vehicleMaintenanceService";
import * as vehicleMaintenanceServiceReminder from "../services/vehicleMaintenanceReminderService";
import { VehicleMaintenanceReminderDto, VehicleMaintenanceReminderQueryParameters } from "../models/vehicle/vehicleMaintenanceReminder";

import { loadVehicleCameras } from "../services/vehicleMediaService";
import { FlespiVehicleCameraData } from "../models/vehicle/flespiDataTypes";

export interface VehicleState {
    vehicles: VehicleListItemDto[];
    options: IMuiSelectOption[];
    allVehicleGroupsLoading: boolean;
    allVehicleGroups: VehicleGroupItem[];
    vehicleCameras: FlespiVehicleCameraData[];
    vehicleCamerasLoading: boolean;
    vehicleMaintenances?: VehicleMaintenanceDto[];
    vehicleMaintenancesLoading: boolean;
    vehicleMaintenanceReminders?: VehicleMaintenanceReminderDto[];
    vehicleMaintenanceRemindersLoading: boolean;
}

export const initialState: VehicleState = {
    vehicles: null,
    options: null,
    allVehicleGroupsLoading: true,
    allVehicleGroups: [],
    vehicleCameras: null,
    vehicleCamerasLoading: false,
    vehicleMaintenances: [],
    vehicleMaintenancesLoading: false,
    vehicleMaintenanceReminders: [],
    vehicleMaintenanceRemindersLoading: false,
};

export const vehicleSlice = createSlice({
    name: "vehicle",
    initialState,
    reducers: {
        setVehicles: (state, action: PayloadAction<VehicleListItemDto[]>) => {
            state.vehicles = action.payload;
        },
        setOptions: (state, action: PayloadAction<IMuiSelectOption[]>) => {
            state.options = action.payload;
        },
        setAllVehicleGroups(state, action: PayloadAction<VehicleGroupItem[]>) {
            state.allVehicleGroups = action.payload;
        },
        setAllVehicleCameras(state, action: PayloadAction<FlespiVehicleCameraData[]>) {
            state.vehicleCameras = action.payload;
        },
        setVehicleMaintenances(state, action: PayloadAction<VehicleMaintenanceDto[]>) {
            state.vehicleMaintenances = action.payload;
        },
        setVehicleMaintenanceReminders(state, action: PayloadAction<VehicleMaintenanceReminderDto[]>) {
            state.vehicleMaintenanceReminders = action.payload;
        },
        setVehicleMaintenancesLoading(state, action: PayloadAction<boolean>) {
            state.vehicleMaintenancesLoading = action.payload;
        },
        setVehicleMaintenanceRemindersLoading(state, action: PayloadAction<boolean>) {
            state.vehicleMaintenanceRemindersLoading = action.payload;
        },
        removeVehicleMaintenance(state, action: PayloadAction<string>) {
            state.vehicleMaintenances = state.vehicleMaintenances?.filter(vm => vm.id !== action.payload);
            return state;
        },
        removeVehicleMaintenanceReminder(state, action: PayloadAction<string>) {
            state.vehicleMaintenanceReminders = state.vehicleMaintenanceReminders?.filter(vm => vm.id !== action.payload);
            return state;
        },
        addVehicleMaintenance(state, action: PayloadAction<VehicleMaintenanceDto>) {
            state.vehicleMaintenances.push(action.payload);
        },
        updateVehicleMaintenance(state, action: PayloadAction<VehicleMaintenanceDto>) {
            state.vehicleMaintenances = state.vehicleMaintenances.map((item: VehicleMaintenanceDto) => {
                if (item.id !== action.payload.id) return item;

                console.log(JSON.stringify(action.payload));
                return {
                    ...item,
                    ...action.payload
                };
            });
            return state;
        }
    }, extraReducers(builder) {
        builder
            .addCase(fetchAllVehicleGroups.pending, (state) => {
                state.allVehicleGroupsLoading = true;
            })
            .addCase(fetchAllVehicleGroups.fulfilled, (state, action) => {
                state.allVehicleGroupsLoading = false;
                state.allVehicleGroups = action.payload;
            })
            .addCase(fetchAllVehicleGroups.rejected, (state) => {
                state.allVehicleGroupsLoading = false;
            })
            .addCase(fetchAllVehicleCameras.pending, (state) => {
                state.vehicleCamerasLoading = true;
            })
            .addCase(fetchAllVehicleCameras.fulfilled, (state, action) => {
                state.vehicleCamerasLoading = false;
                state.vehicleCameras = action.payload;
            })
            .addCase(fetchAllVehicleCameras.rejected, (state) => {
                state.vehicleCamerasLoading = false;
            })
            .addCase(saveVehicleMaintenance.fulfilled, (state, action) => {
                const index = state.vehicleMaintenances.findIndex(i => i.id === action.payload.id);

                // Update vehicle maintenances by replacing existing or adding new
                if (index >= 0) {
                    state.vehicleMaintenances[index] = action.payload;
                } else {
                    state.vehicleMaintenances.push(action.payload);
                }
            })
            .addCase(saveVehicleMaintenanceReminder.fulfilled, (state, action) => {
                const index = state.vehicleMaintenanceReminders.findIndex(i => i.id === action.payload.id);

                // Update vehicle maintenances by replacing existing or adding new
                if (index >= 0) {
                    state.vehicleMaintenanceReminders[index] = action.payload;
                } else {
                    state.vehicleMaintenanceReminders.push(action.payload);
                }
            })
            .addCase(approveVehicleMaintenanceReminderData.fulfilled, (state, action) => {
                const index = state.vehicleMaintenanceReminders.findIndex(i => i.id === action.payload.id);
                if (index >= 0) {
                    state.vehicleMaintenanceReminders[index] = action.payload;
                }
            });
    },
});

export const {
    setVehicles,
    setOptions,
    setAllVehicleGroups,
    setVehicleMaintenances,
    removeVehicleMaintenance,
    addVehicleMaintenance,
    updateVehicleMaintenance,
    setVehicleMaintenancesLoading,
    setAllVehicleCameras,
    setVehicleMaintenanceReminders,
    setVehicleMaintenanceRemindersLoading,
    removeVehicleMaintenanceReminder
} = vehicleSlice.actions;

export const fetchVehicleOptions = createAsyncThunk(
    "vehicle/options",
    async(_, thunkApi) => {
        const vehicles = await getVehicles(false, thunkApi.signal);
        thunkApi.dispatch(setVehicles(vehicles.data));
        thunkApi.dispatch(
            setOptions(
                vehicles.data.map((v) => ({
                    value: v.id,
                    label: `${v.registerNumber}, ${v.brand}`,
                }))
            )
        );
    }
);

export const fetchAllVehicleGroups = createAsyncThunk(
    "monitorView/fetchAllVehicleGroups",
    async(request: string[], { rejectWithValue }) => {
        try {
            const response = await getVehicleGroups(request);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle groups");
        }
    }
);

export const fetchAllVehicleCameras = createAsyncThunk(
    "vehicle/fetchAllVehicleCameras",
    async (request: void, { rejectWithValue }) => {
        try {
            const response = await loadVehicleCameras();
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle groups");
        }
    }
);

/* Vehicle maintenance actions */
export const fetchVehicleMaintenanceData = createAsyncThunk(
    "vehicle/fetchVehicleMaintenanceData",
    async(
        {
            fromDate,
            untilDate,
            vehicleIds
        }: {
            fromDate: Date;
            untilDate?: Date;
            vehicleIds?: string[];
        },
        _thunkApi) => {
        _thunkApi.dispatch(setVehicleMaintenancesLoading(true));
        const maintenanceData = await vehicleMaintenanceService.getVehicleMaintenances(fromDate, untilDate, vehicleIds, _thunkApi.signal);
        _thunkApi.dispatch(setVehicleMaintenances(maintenanceData));
        _thunkApi.dispatch(setVehicleMaintenancesLoading(false));
    }
);

export const saveVehicleMaintenance = createAsyncThunk(
    "vehicle/saveVehicleMaintenance",
    async(
        {
            vehicleMaintenanceDto }:
            {
                vehicleMaintenanceDto: VehicleMaintenanceDto;
            },
        _thunkApi) => {
        const res = await vehicleMaintenanceService.saveVehicleMaintenance(vehicleMaintenanceDto, _thunkApi.signal);
        return res.data;
    }
);

export const deleteVehicleMaintenanceData = createAsyncThunk(
    "vehicle/deleteVehicleMaintenanceData",
    async(
        {
            vehicleMaintenanceId
        }: {
            vehicleMaintenanceId: string;
        },
        _thunkApi) => {
        await vehicleMaintenanceService.deleteVehicleMaintenance(vehicleMaintenanceId, _thunkApi.signal);

        _thunkApi.dispatch(removeVehicleMaintenance(vehicleMaintenanceId));
    }
);

export const fetchVehicleMaintenanceReminderData = createAsyncThunk(
    "vehicle/fetchVehicleMaintenanceReminderData",
    async(
        qsp: VehicleMaintenanceReminderQueryParameters,
        _thunkApi) => {
        _thunkApi.dispatch(setVehicleMaintenanceRemindersLoading(true));
        const maintenanceData = await vehicleMaintenanceServiceReminder.getVehicleMaintenanceReminders(
            qsp,
            _thunkApi.signal
        );
        _thunkApi.dispatch(setVehicleMaintenanceReminders(maintenanceData));
        _thunkApi.dispatch(setVehicleMaintenanceRemindersLoading(false));
    }
);

export const saveVehicleMaintenanceReminder = createAsyncThunk(
    "vehicle/saveVehicleMaintenanceReminder",
    async(
        {
            vehicleMaintenanceReminderDto }:
            {
                vehicleMaintenanceReminderDto: VehicleMaintenanceReminderDto;
            },
        _thunkApi) => {
        const res = await vehicleMaintenanceServiceReminder.saveVehicleMaintenanceReminder(vehicleMaintenanceReminderDto, _thunkApi.signal);
        return res.data;
    }
);

export const approveVehicleMaintenanceReminderData = createAsyncThunk(
    "vehicle/approveVehicleMaintenanceReminder",
    async(
        {
            vehicleMaintenanceId
        }: {
            vehicleMaintenanceId: string;
        },
        _thunkApi) => {
        const res = await vehicleMaintenanceServiceReminder.approveVehicleMaintenanceReminder(vehicleMaintenanceId, _thunkApi.signal);
        return res.data;
    }
);

export const deleteVehicleMaintenanceReminderData = createAsyncThunk(
    "vehicle/deleteVehicleMaintenanceReminderData",
    async(
        {
            vehicleMaintenanceReminderId
        }: {
            vehicleMaintenanceReminderId: string;
        },
        _thunkApi) => {
        await vehicleMaintenanceServiceReminder.deleteVehicleMaintenanceReminder(vehicleMaintenanceReminderId, _thunkApi.signal);

        _thunkApi.dispatch(removeVehicleMaintenanceReminder(vehicleMaintenanceReminderId));
    }
);

export const vehicleReducer = vehicleSlice.reducer;
